import ChatIcon from '@mui/icons-material/Chat'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded'
import InfoIcon from '@mui/icons-material/Info'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined'
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded'
import TelegramIcon from '@mui/icons-material/Telegram'
import AC from 'agora-chat'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppUppdatePopupContext } from 'src/components/AppUpdate/AppUpdatePopupContext'
import { isStandaloneAppMode } from 'src/components/AppUpdate/appRefreshUtils'
import { AGORA_CHAT_MESSAGE_CUSTOM_EXTS_TYPE } from 'src/consumer/constants'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { appVersion, lastBuildDate } from 'src/consumer/services'
import {
  selectAgoraChatId,
  selectAgoraChatServerConnection,
  selectAgoraChatToken,
  setAgoraChatId,
  setAgoraChatServerConnection,
  setAgoraChatToken,
} from 'src/consumer/reducers/personalData/personalDataReducer'
import {
  getAgoraChatToken,
  getConversationWithThisUser,
  informBookingRequestReceivedForMentor,
} from 'src/consumer/services/featureServices'
import { getData } from 'src/consumer/services/profileService'
import { useScreenSize } from 'src/customHooks/customHooks'
import { getImageLink, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { addReceivedBookingRequest } from 'src/mentor/reducers/bookingRequest/bookingRequestReducer'
import { getPortalVoiceOptions } from 'src/mentor/services/portalVoiceCallService'
import {
  dismissPortalVoiceCall,
  endPortalVoiceCall,
  extendPortalVoiceCall,
  getPortalVoiceCallState,
  getPortalVoiceFriendlyErrorMessage,
  startPortalVoiceCall,
  subscribePortalVoiceCallState,
} from 'src/mentor/components/voice/portalVoiceCallManager'
import {
  getPortalVoiceLauncherContext,
  PORTAL_VOICE_LAUNCHER_CONTEXT_EVENT,
} from 'src/mentor/components/voice/portalVoiceLauncherContext'
import SupportWhatsappThreadsPanel from 'src/supportPortal/components/contact/SupportWhatsappThreadsPanel'
import ConversationListChatUI from '../ChatUIComponents/ConversationListChatUI'
import {
  isOnlineNow,
  replaceTriedSentMessageWithResultantMessage,
  sortMessagesArray,
} from '../ChatUIComponents/helperFunctions'
import TwoPeopleChatUI from '../ChatUIComponents/TwoPeopleChatUI'
import { isSameUserId } from './helperFunctions'
import { FIButtonContainer, FIContentContainer } from './intercomStyles'

function formatRemainingTime(seconds) {
  const safeSeconds = Math.max(0, Number(seconds) || 0)
  const hours = Math.floor(safeSeconds / 3600)
  const minutes = Math.floor((safeSeconds % 3600) / 60)
  const secs = safeSeconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

function formatMinutesLabel(seconds) {
  const minutes = Math.max(1, Math.round((Number(seconds) || 0) / 60))
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

function formatElapsedTime(seconds) {
  const safeSeconds = Math.max(0, Number(seconds) || 0)
  const minutes = Math.floor(safeSeconds / 60)
  const secs = safeSeconds % 60
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

function getProviderBadge(providerType) {
  if (providerType === 'plivo') {
    return '(P)'
  }
  if (providerType === 'twilio') {
    return '(T)'
  }
  return ''
}

function getVoiceStatusPresentation(callState = {}) {
  switch (callState?.status) {
    case 'connecting':
      return { label: 'Preparing call', border: '#2563eb', background: '#eff6ff', color: '#1d4ed8' }
    case 'ringing':
      return { label: 'Ringing', border: '#f59e0b', background: '#fff7ed', color: '#92400e' }
    case 'connected':
      return { label: 'Live', border: '#16a34a', background: '#f0fdf4', color: '#15803d' }
    case 'reconnecting':
      return { label: 'Reconnecting', border: '#f59e0b', background: '#fff7ed', color: '#92400e' }
    case 'ending':
      return { label: 'Ending', border: '#94a3b8', background: '#f8fafc', color: '#475569' }
    case 'on-hold':
      return { label: 'On hold', border: '#7c3aed', background: '#f5f3ff', color: '#6d28d9' }
    case 'ended':
      return { label: 'Ended', border: '#94a3b8', background: '#f8fafc', color: '#475569' }
    default:
      return { label: 'Idle', border: '#94a3b8', background: '#f8fafc', color: '#475569' }
  }
}

function floatingChipStyles(borderColor, backgroundColor, textColor) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px 10px',
    borderRadius: '999px',
    border: `1px solid ${borderColor}`,
    background: backgroundColor,
    color: textColor,
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1.2,
  }
}

function floatingActionButtonStyles({ primary = false, disabled = false, tone = 'brand' } = {}) {
  const baseTone =
    tone === 'danger'
      ? {
          background: '#dc2626',
          color: '#ffffff',
          border: '1px solid #dc2626',
        }
      : primary
        ? {
            background: '#1d4ed8',
            color: '#ffffff',
            border: '1px solid #1d4ed8',
          }
        : {
            background: '#ffffff',
            color: '#0f172a',
            border: '1px solid rgba(148, 163, 184, 0.65)',
          }

  return {
    flex: primary ? '0 0 auto' : '1 1 auto',
    minHeight: '42px',
    padding: '0 14px',
    borderRadius: '12px',
    fontWeight: 700,
    fontSize: '14px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    ...baseTone,
  }
}

const floatingEmptyStateStyles = {
  minHeight: '180px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  textAlign: 'center',
  color: '#64748b',
  fontWeight: 600,
  lineHeight: 1.5,
  background: '#ffffff',
  borderRadius: '18px',
  border: '1px solid rgba(226, 232, 240, 1)',
}

const floatingRouteCardStyles = {
  width: '100%',
  background: '#ffffff',
  border: '1px solid rgba(226, 232, 240, 1)',
  borderRadius: '18px',
  padding: '16px',
  boxShadow: '0 14px 30px rgba(15, 23, 42, 0.06)',
}

const portalVoiceMotionStyles = `
  @keyframes portalVoiceWave {
    0%, 100% { transform: scaleY(0.38); opacity: 0.4; }
    50% { transform: scaleY(1); opacity: 1; }
  }

  @keyframes portalVoiceGlow {
    0%, 100% { transform: scale(0.96); opacity: 0.32; }
    50% { transform: scale(1.04); opacity: 0.55; }
  }

  @keyframes portalVoiceRing {
    0%, 100% { transform: scale(1); opacity: 0.38; }
    50% { transform: scale(1.08); opacity: 0.12; }
  }
`

function getVoicePanelDescription({
  hasActiveVoiceCall,
  hasSettledVoiceCall,
  voiceLauncherContext,
  showVoiceLauncherTab,
}) {
  if (hasActiveVoiceCall) {
    return 'Stay here to track the call, extend it when allowed, or hang up without losing context.'
  }
  if (hasSettledVoiceCall) {
    return 'Review the last call outcome here or start another call when needed.'
  }
  if (voiceLauncherContext?.clinicBrandId && voiceLauncherContext?.clinicId) {
    return 'Quickly call the clinic assistant or support team for this clinic.'
  }
  if (voiceLauncherContext?.bookedEventId) {
    return 'Quickly call the patient or clinic team for this session.'
  }
  if (showVoiceLauncherTab) {
    return 'Call options appear here when this screen provides a clinic or session context.'
  }
  return 'Calling options are not available on this screen.'
}

function VoiceWaveform({ status, accentColor }) {
  const bars = [16, 26, 36, 30, 22, 34, 18]
  const isAnimated = ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold'].includes(
    status,
  )
  const ringTone =
    status === 'ringing'
      ? 'portalVoiceRing 1.4s ease-in-out infinite'
      : isAnimated
        ? 'portalVoiceGlow 1.9s ease-in-out infinite'
        : 'none'

  return (
    <div
      style={{
        position: 'relative',
        width: '110px',
        height: '110px',
        borderRadius: '28px',
        background: `radial-gradient(circle at 30% 20%, rgba(255,255,255,0.92), ${accentColor}18 60%, ${accentColor}10 100%)`,
        border: `1px solid ${accentColor}33`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: '16px',
          borderRadius: '24px',
          border: `1px solid ${accentColor}22`,
          animation: ringTone,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '5px',
          height: '54px',
        }}
      >
        {bars.map((height, index) => (
          <span
            key={`${status || 'idle'}-${index}`}
            style={{
              width: '7px',
              height: `${height}px`,
              borderRadius: '999px',
              background: accentColor,
              opacity: isAnimated ? 0.95 : 0.45,
              transformOrigin: 'center bottom',
              animation: isAnimated ? 'portalVoiceWave 1.15s ease-in-out infinite' : 'none',
              animationDelay: `${index * 0.08}s`,
            }}
          />
        ))}
      </div>
    </div>
  )
}

export default function FloatingIntercomButton() {
  const { profileData } = useContext(ProfileContext)
  const appUpdateContext = useContext(AppUppdatePopupContext)
  const { width } = useScreenSize()
  const { pathname } = useLocation()
  const agoraAppKey = import.meta.env.VITE_AGORA_CHAT_APPKEY
  const navigate = useNavigate()
  const isStandaloneInstalledApp = isStandaloneAppMode()
  const reSendAppUpdateAction = appUpdateContext?.reSendAppUpdateAction
  const requestAppRefresh = appUpdateContext?.requestAppRefresh
  const isAppRefreshInProgress = Boolean(appUpdateContext?.isAppRefreshInProgress)
  const isMentorPortalPath = pathname.startsWith('/mentor-home')
  const isClinicAssistantPortalPath = pathname.startsWith('/ca-home') || pathname.startsWith('/kiosk')
  const isSupportPortalPath = pathname.startsWith('/support-home') || pathname.startsWith('/support/')
  const isLiveSessionShortUrl = pathname.startsWith('/l/')
  const isLiveSession = pathname.startsWith('/live/')
  const showVoiceLauncherTab =
    isMentorPortalPath || isClinicAssistantPortalPath || isLiveSessionShortUrl || isLiveSession

  const dispatch = useDispatch()
  const agoraChatServerConnection = useSelector(selectAgoraChatServerConnection)
  const agoraChatId = useSelector(selectAgoraChatId)
  const agoraChatToken = useSelector(selectAgoraChatToken)
  const [selectedTab, setSelectedTab] = useState(() =>
    showVoiceLauncherTab ? 'calls' : isSupportPortalPath ? 'threads' : isStandaloneInstalledApp ? 'app' : 'chat',
  )

  const [floatingButtonVisible, setFloatingButtonVisible] = useState(false)
  const [open, setOpen] = useState(false)
  const [chatBootstrapped, setChatBootstrapped] = useState(false)
  const [contactsLoaded, setContactsLoaded] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [intercomState, setIntercomState] = useState('conversationList')
  const [chosenPersonChatId, setChosenPersonChatId] = useState()
  const [userOnlineList, setUserOnlineList] = useState([])

  const [contacts, setContacts] = useState([])
  const [conversationsWithContacts, setConversationsWithContacts] = useState({})
  const [activePageNumbersOfConversations, setActivePageNumbersOfConversations] = useState({})
  const [chatsLoading, setChatsLoading] = useState(false)
  const [isCheckingAppUpdate, setIsCheckingAppUpdate] = useState(false)
  const [lastCheckedForUpdateAt, setLastCheckedForUpdateAt] = useState(null)
  const [voiceLauncherContext, setVoiceLauncherContext] = useState(() =>
    getPortalVoiceLauncherContext(),
  )
  const [voiceOptionsState, setVoiceOptionsState] = useState({ routes: [], error: '' })
  const [voiceLoadingOptions, setVoiceLoadingOptions] = useState(false)
  const [voiceLoadingRoute, setVoiceLoadingRoute] = useState('')
  const [voiceCallState, setVoiceCallState] = useState(getPortalVoiceCallState())

  const [scrollTriggerBoolean, setScrollTriggerBoolean] = useState(false)
  const availableTabs = useMemo(() => {
    const tabs = []

    if (showVoiceLauncherTab) {
      tabs.push('calls')
    }
    if (isSupportPortalPath) {
      tabs.push('threads')
    }
    tabs.push('chat', 'info')
    if (isStandaloneInstalledApp) {
      tabs.push('app')
    }

    return tabs
  }, [isStandaloneInstalledApp, isSupportPortalPath, showVoiceLauncherTab])

  const hasActiveVoiceCall = [
    'connecting',
    'ringing',
    'connected',
    'reconnecting',
    'on-hold',
    'ending',
  ].includes(voiceCallState?.status)
  const hasSettledVoiceCall = voiceCallState?.status === 'ended'
  const voiceStatusPresentation = getVoiceStatusPresentation(voiceCallState)
  const voiceRoutes = Array.isArray(voiceOptionsState?.routes) ? voiceOptionsState.routes : []
  const voiceAllowedDestinationTypes = Array.isArray(voiceLauncherContext?.allowedDestinationTypes)
    ? voiceLauncherContext.allowedDestinationTypes
    : []
  const filteredVoiceRoutes =
    voiceAllowedDestinationTypes.length > 0
      ? voiceRoutes.filter((route) =>
          voiceAllowedDestinationTypes.includes(route?.destinationType || route?.id),
        )
      : voiceRoutes
  const voiceRtcConflict =
    !hasActiveVoiceCall && Boolean(voiceLauncherContext?.rtcConflict?.active)
      ? voiceLauncherContext?.rtcConflict
      : null
  const voicePanelDescription = getVoicePanelDescription({
    hasActiveVoiceCall,
    hasSettledVoiceCall,
    voiceLauncherContext,
    showVoiceLauncherTab,
  })

  useEffect(() => subscribePortalVoiceCallState(setVoiceCallState), [])

  useEffect(() => {
    const handleVoiceLauncherContextChange = (event) => {
      setVoiceLauncherContext(event?.detail || null)
    }

    setVoiceLauncherContext(getPortalVoiceLauncherContext())
    window.addEventListener(PORTAL_VOICE_LAUNCHER_CONTEXT_EVENT, handleVoiceLauncherContextChange)

    return () => {
      window.removeEventListener(
        PORTAL_VOICE_LAUNCHER_CONTEXT_EVENT,
        handleVoiceLauncherContextChange,
      )
    }
  }, [])

  useEffect(() => {
    setVoiceLauncherContext(getPortalVoiceLauncherContext())
  }, [pathname])

  useEffect(() => {
    if (availableTabs.includes(selectedTab)) {
      return
    }

    setSelectedTab(availableTabs[0] || 'chat')
  }, [availableTabs, selectedTab])

  useEffect(() => {
    if (showVoiceLauncherTab) {
      setSelectedTab((prev) => (prev === 'calls' ? prev : 'calls'))
      return
    }

    setSelectedTab((prev) => {
      if (prev !== 'calls') {
        return prev
      }

      return isStandaloneInstalledApp ? 'app' : 'chat'
    })
  }, [isStandaloneInstalledApp, showVoiceLauncherTab])

  useEffect(() => {
    if (!showVoiceLauncherTab || (!hasActiveVoiceCall && !hasSettledVoiceCall)) {
      return
    }
    setSelectedTab('calls')
    setOpen(true)
  }, [hasActiveVoiceCall, hasSettledVoiceCall, showVoiceLauncherTab])

  // chat sound function
  const playRequestIncomingSound = async () => {
    const soundModule = await import('src/assets/audio/therapy_notification.mp3')
    const sound = new window.Audio(soundModule.default)
    sound.play()
  }

  // 1
  async function getAndSetContacts() {
    const isWorkspacePath = pathname.startsWith('/workplace')
    const isStaticSupportUser =
      profileData?.id === 'USR-09290ce5379c49b6a97aed9734068fc4' ||
      profileData?.id === 'USR-d67006790d0d49a4bb43dca582a4eb0d'

    if (!chatBootstrapped || isWorkspacePath || isStaticSupportUser || !profileData) {
      return
    }

    setContactsLoaded(false)
    const response = await getData('/api/secure/user-chat/contacts')
    if (response?.status === 200 && Array.isArray(response?.data) && response?.data?.length > 0) {
      setContacts(response?.data?.filter((item) => !isSameUserId(item?.userId, profileData?.id)))
    } else {
      setContacts([])
    }
    setContactsLoaded(true)
  }
  useEffect(() => {
    if (!chatBootstrapped) {
      return
    }

    getAndSetContacts()
  }, [chatBootstrapped, pathname, profileData])

  // 2 : SETTING VISIBILITY
  useEffect(() => {
    if (isStandaloneInstalledApp) {
      setFloatingButtonVisible(true)
      return
    }

    const isMentorHotlinePath = pathname.startsWith('/hotline-for-mentor')
    const isClinicOwnerPortalPath = pathname.startsWith('/clinic-home')
    const isWorkspacePath = pathname.startsWith('/workplace')
    const isSupportPortalPath = pathname.startsWith('/support-home')
    const isCounterScreenPath = pathname.includes('counter-screen')
    const isChamberRoomScreen = pathname.includes('chamber-screen-home')
    if (
      isMentorHotlinePath ||
      isMentorPortalPath ||
      isClinicAssistantPortalPath ||
      isClinicOwnerPortalPath ||
      isWorkspacePath ||
      isSupportPortalPath ||
      isLiveSessionShortUrl ||
      isLiveSession ||
      profileData?.id === 'USR-09290ce5379c49b6a97aed9734068fc4' ||
      profileData?.id === 'USR-d67006790d0d49a4bb43dca582a4eb0d'
    ) {
      if (isCounterScreenPath || isChamberRoomScreen) {
        setFloatingButtonVisible(false)
      } else {
        setFloatingButtonVisible(true)
      }
    } else {
      setFloatingButtonVisible(false)
    }
  }, [isStandaloneInstalledApp, pathname, profileData?.id])

  useEffect(() => {
    if (!isStandaloneInstalledApp && selectedTab === 'app') {
      setSelectedTab('chat')
    }
  }, [isStandaloneInstalledApp, selectedTab])

  // 3 : SETTING CONTACTS MANUALLY - TEMPORARY - SHOULD COME FROM SERVER
  useEffect(() => {
    if (!chatBootstrapped) {
      return
    }

    const isWorkspacePath = pathname.startsWith('/workplace')
    if (isWorkspacePath) {
      setContacts([
        {
          relativeRole: 'Your Success Manager',
          profilePicId: 'MEDIA-4a8eeb80da0842099ad7755f4682d557',
          authorities: ['ROLE_USER'],
          firstName: 'Aditya Verma',
          name: 'Aditya Verma',
          userId: 'USR-09290ce5379c49b6a97aed9734068fc4',
          userName: 'aumhum.successmanager@aumhum.xyz',
          userNumber: 'USR001156080',
        },
        {
          // relativeRole: '',
          profilePicId: 'MEDIA-ed39ae4f4af94272a4bf3e63ed4172a9',
          authorities: ['ROLE_USER'],
          firstName: 'Success Bot',
          name: 'Success Bot',
          userId: 'USR-d67006790d0d49a4bb43dca582a4eb0d',
          userName: 'aumhum.bot@aumhum.xyz',
          userNumber: 'USR001156079',
        },
      ])
      setContactsLoaded(true)
      return
    }
    if (
      profileData?.id === 'USR-09290ce5379c49b6a97aed9734068fc4' ||
      profileData?.id === 'USR-d67006790d0d49a4bb43dca582a4eb0d'
    ) {
      setContacts([
        {
          relativeRole: 'Aumhum Business Client',
          profilePicId: 'MEDIA-77327e52622844259b4b014ac10b0f16',
          authorities: ['ROLE_USER'],
          firstName: 'Dhawal Sharma',
          name: 'Dhawal Sharma',
          userId: 'USR-d1ebd7aff0c948a8985114bee64f49d9',
          userName: 'aumhum.customer@outlook.com',
          userNumber: 'USR001142886',
        },
      ])
      setContactsLoaded(true)
    }
  }, [chatBootstrapped, pathname, profileData?.id])

  // 4 : ALL IMPORTANT CONNECTION MAKING AND REDUX STUFF
  useEffect(() => {
    if (!chatBootstrapped) {
      return
    }

    // make a connection to agora only when needed (when there are contacts) and store the connection in redux
    if (contacts?.length > 0) {
      // 1. check if redux already data - conn, userid, token
      if (agoraChatServerConnection && agoraChatId && agoraChatToken) {
        // console.log('there is data')
      } else {
        // 2. call the function that does this
        makeAndStoreAgoraConnectionInRedux(dispatch, agoraAppKey, profileData?.id)
      }
    }
  }, [chatBootstrapped, contacts, agoraChatServerConnection, agoraChatId, agoraChatToken, profileData])

  // 5 : Put agoraChatServerConnnection in dependency array -- if that changes -- and now exists --
  // --send my presence every N seconds to agora server -- presence sending should be from one place only -- even if connection is made because of video call and not contacts
  function keepSendingPresenceOnline() {
    if (agoraChatServerConnection) {
      agoraChatServerConnection
        .publishPresence({ description: `online@${dayjs().unix()}` })
        .then((res) => {
          // console.log(res)
        })
        .catch((error) => console.log(error))
    }
  }
  useEffect(() => {
    if (!chatBootstrapped || !agoraChatServerConnection) {
      return
    }

    // Initial update on mount
    keepSendingPresenceOnline()
    // Interval to update presence status every 30 seconds
    const presenceInterval = setInterval(() => {
      keepSendingPresenceOnline()
    }, 30000) // 30 seconds
    // Cleanup function to clear interval on unmount
    return () => {
      clearInterval(presenceInterval)
    }
  }, [chatBootstrapped, agoraChatServerConnection])

  // 6. Check presence of contacts every 30 seconds
  function keepCheckingPresenceOfContacts() {
    if (agoraChatServerConnection && contacts?.length > 0) {
      const userIdsToCheck = []
      contacts?.forEach((contact) => {
        // agora only sends userids in lowercase, so we ask for lowercase userids
        userIdsToCheck.push(contact?.userId?.toLowerCase())
      })
      let option = {
        usernames: userIdsToCheck,
      }
      agoraChatServerConnection.getPresenceStatus(option).then((res) => {
        const tempOnlineOfflineArray = []
        res?.data?.result?.forEach((resultItem) => {
          let preparedObject = {}
          preparedObject.userId = resultItem.uid
          preparedObject.online = resultItem.ext ? isOnlineNow(resultItem.ext?.slice(7)) : false
          tempOnlineOfflineArray.push(preparedObject)
        })
        setUserOnlineList(tempOnlineOfflineArray)
      })
    }
  }
  useEffect(() => {
    if (!chatBootstrapped) {
      return
    }

    // Initial update on mount
    keepCheckingPresenceOfContacts()
    // Interval to update presence status every 30 seconds
    const presenceInterval = setInterval(() => {
      keepCheckingPresenceOfContacts()
    }, 30000) // 30 seconds
    // Cleanup function to clear interval on unmount
    return () => {
      clearInterval(presenceInterval)
    }
  }, [chatBootstrapped, agoraChatServerConnection, contacts])

  const otherChosenUser = contacts?.find((contact) =>
    isSameUserId(contact?.userId, chosenPersonChatId),
  )

  // 7. chats of all contacts
  async function getChatDataOfContacts() {
    if (agoraChatServerConnection && contacts?.length > 0) {
      const chatData = {} // Object to store chat data for each contact
      const pageNumberData = {} // Object to store page number of each
      for (const contact of contacts) {
        const { userId } = contact
        setChatsLoading(true)
        const conversationResponse = await getConversationWithThisUser(userId, 1)
        setChatsLoading(false)
        if (conversationResponse?.status === 200 && Array.isArray(conversationResponse?.data)) {
          chatData[userId] = sortMessagesArray(conversationResponse?.data)
        } else {
          chatData[userId] = []
        }
        pageNumberData[userId] = 1
      }
      setConversationsWithContacts(chatData)
      setActivePageNumbersOfConversations(pageNumberData)
    }
  }
  useEffect(() => {
    if (!chatBootstrapped) {
      return
    }

    getChatDataOfContacts()
  }, [chatBootstrapped, agoraChatServerConnection, contacts])

  // 8. handler to listen to incoming messages
  useEffect(() => {
    if (chatBootstrapped && agoraChatServerConnection && agoraChatId && agoraChatToken) {
      agoraChatServerConnection?.addEventHandler?.('message', {
        // Occurs when a message is received
        onTextMessage: function (message) {
          if (isSameUserId(message?.to, agoraChatId)) {
            const userIdOfConversationToUpdate = Object.keys(conversationsWithContacts)?.find(
              (contactId) => isSameUserId(contactId, message?.from),
            )
            if (userIdOfConversationToUpdate) {
              const existingArray = conversationsWithContacts[userIdOfConversationToUpdate] || []
              setConversationsWithContacts((prev) => ({
                ...prev,
                [userIdOfConversationToUpdate]: [...existingArray, message],
              }))
              setScrollTriggerBoolean((prev) => !prev)
            }
          }
        },
        onCustomMessage: async function (message) {
          if (isSameUserId(message?.to, agoraChatId)) {
            if (
              message?.customExts?.type ===
                AGORA_CHAT_MESSAGE_CUSTOM_EXTS_TYPE.INCOMING_BOOKING_REQUEST ||
              message?.customExts?.type ===
                AGORA_CHAT_MESSAGE_CUSTOM_EXTS_TYPE.BOOKING_REQUEST_CANCELLED ||
              message?.customExts?.type ===
                AGORA_CHAT_MESSAGE_CUSTOM_EXTS_TYPE.BOOKING_REQUEST_ACCEPTED
            ) {
              dispatch(
                addReceivedBookingRequest({
                  ...message?.customExts?.bookingRequest,
                  type: message?.customExts?.type,
                }),
              )
              if (
                message?.customExts?.type ===
                AGORA_CHAT_MESSAGE_CUSTOM_EXTS_TYPE.INCOMING_BOOKING_REQUEST
              ) {
                toast.info('New booking request received')
                await informBookingRequestReceivedForMentor(
                  message?.customExts?.bookingRequest?.id,
                  'chat',
                  'agora',
                )
                try {
                  playRequestIncomingSound()
                } catch (e) {
                  console.log(e)
                }
                navigate('/booking-requests-for-mentors/active')
              }
            }

            // adding to messages array
            const userIdOfConversationToUpdate = Object.keys(conversationsWithContacts)?.find(
              (contactId) => isSameUserId(contactId, message?.from),
            )
            if (userIdOfConversationToUpdate) {
              const existingArray = conversationsWithContacts[userIdOfConversationToUpdate] || []
              setConversationsWithContacts((prev) => ({
                ...prev,
                [userIdOfConversationToUpdate]: [...existingArray, message],
              }))
              setScrollTriggerBoolean((prev) => !prev)
            }
          }
        },
      })
    }
    return () => {
      if (agoraChatServerConnection) {
        agoraChatServerConnection?.removeEventHandler?.('message')
      }
    }
  }, [chatBootstrapped, agoraChatServerConnection, agoraChatId, agoraChatToken, conversationsWithContacts])

  // 9. load more chats of a user
  async function loadMoreChats(userId) {
    if (Array.isArray(conversationsWithContacts?.[userId])) {
      const allOlderMessagesAscTime = conversationsWithContacts?.[userId]
      const pageNumberActiveInThisConversation = activePageNumbersOfConversations?.[userId]
      if (allOlderMessagesAscTime.length > 0) {
        setChatsLoading(true)
        const conversationResponse = await getConversationWithThisUser(
          userId,
          pageNumberActiveInThisConversation + 1,
        )
        setChatsLoading(false)
        if (
          conversationResponse?.status === 200 &&
          Array.isArray(conversationResponse?.data) &&
          conversationResponse?.data?.length > 0
        ) {
          setConversationsWithContacts((prev) => ({
            ...prev,
            [userId]: sortMessagesArray([...conversationResponse.data, ...(prev[userId] || [])]),
          }))
          setActivePageNumbersOfConversations((prev) => ({
            ...prev,
            [userId]: pageNumberActiveInThisConversation + 1,
          }))
        } else {
          console.log('no array found, some problem')
        }
      }
    }
  }

  // 10. replace message in messages array of a user id
  async function replaceSpecificTempMessageWithResultMessage(
    userId,
    tempMsgIdToReplace,
    resultMessage,
  ) {
    setConversationsWithContacts((prev) => {
      const newArrayOfMessagesForThisUser = replaceTriedSentMessageWithResultantMessage(
        prev?.[userId] || [],
        tempMsgIdToReplace,
        resultMessage,
      )
      console.log(prev)
      console.log({ ...prev, [userId]: newArrayOfMessagesForThisUser })
      return { ...prev, [userId]: newArrayOfMessagesForThisUser }
    })
  }

  const isChatConnectionReady =
    contacts?.length === 0 || (agoraChatServerConnection && agoraChatId && agoraChatToken)
  const showChatLoadingState =
    chatBootstrapped && (!contactsLoaded || (contacts?.length > 0 && !isChatConnectionReady))
  const showNoConversationsState = chatBootstrapped && contactsLoaded && contacts?.length === 0

  function ensureChatBootstrapped() {
    if (!chatBootstrapped) {
      setChatBootstrapped(true)
      setContactsLoaded(false)
    }
  }

  async function loadVoiceRoutes() {
    if (!voiceLauncherContext?.actorRole) {
      setVoiceOptionsState({ routes: [], error: '' })
      return { routes: [], error: '' }
    }

    const hasClinicContext = Boolean(voiceLauncherContext?.clinicBrandId && voiceLauncherContext?.clinicId)
    const hasSupportTarget =
      voiceLauncherContext?.actorRole === 'support-agent' &&
      Boolean(voiceLauncherContext?.patientId || voiceLauncherContext?.practitionerId)

    if (!voiceLauncherContext?.bookedEventId && !hasClinicContext && !hasSupportTarget) {
      setVoiceOptionsState({ routes: [], error: '' })
      return { routes: [], error: '' }
    }

    setVoiceLoadingOptions(true)
    const result = await getPortalVoiceOptions({
      actorRole: voiceLauncherContext?.actorRole,
      bookedEventId: voiceLauncherContext?.bookedEventId,
      clinicBrandId: voiceLauncherContext?.clinicBrandId,
      clinicId: voiceLauncherContext?.clinicId,
      practitionerId: voiceLauncherContext?.practitionerId,
      patientId: voiceLauncherContext?.patientId,
      targetRole: voiceLauncherContext?.targetRole,
      eventType: voiceLauncherContext?.eventType,
      eventTypesCsv: voiceLauncherContext?.eventTypesCsv,
    })

    const nextState = {
      routes: result?.routes || [],
      error: result?.ok ? '' : result?.message || 'Unable to load calling options right now.',
    }

    setVoiceOptionsState(nextState)
    setVoiceLoadingOptions(false)
    return nextState
  }

  async function handleVoiceRouteSelect(route) {
    if (!voiceLauncherContext?.actorRole) {
      return
    }

    setVoiceLoadingRoute(route?.destinationType || route?.id || '')
    try {
      await startPortalVoiceCall({
        actorRole: voiceLauncherContext?.actorRole,
        bookedEventId: voiceLauncherContext?.bookedEventId,
        clinicBrandId: voiceLauncherContext?.clinicBrandId,
        clinicId: voiceLauncherContext?.clinicId,
        practitionerId: voiceLauncherContext?.practitionerId,
        patientId: voiceLauncherContext?.patientId,
        targetRole: voiceLauncherContext?.targetRole,
        eventType: voiceLauncherContext?.eventType,
        eventTypesCsv: voiceLauncherContext?.eventTypesCsv,
        destinationType: route?.destinationType || route?.id,
        uiSurface: voiceLauncherContext?.uiSurface || 'floating-intercom-calls',
        providerTypeHint: route?.providerType || null,
      })
    } catch (error) {
      toast.error(
        getPortalVoiceFriendlyErrorMessage(error, 'Unable to place the phone call right now.'),
      )
    } finally {
      setVoiceLoadingRoute('')
    }
  }

  async function handleVoiceCallExtend() {
    try {
      await extendPortalVoiceCall()
    } catch (error) {
      toast.error(error?.message || 'Unable to extend the current phone call.')
    }
  }

  function handleVoiceCallEnd() {
    endPortalVoiceCall()
  }

  useEffect(() => {
    if (!open || selectedTab !== 'calls' || hasActiveVoiceCall) {
      return
    }

    loadVoiceRoutes()
  }, [
    hasActiveVoiceCall,
    open,
    selectedTab,
    voiceLauncherContext?.actorRole,
    voiceLauncherContext?.bookedEventId,
    voiceLauncherContext?.clinicBrandId,
    voiceLauncherContext?.clinicId,
    voiceLauncherContext?.practitionerId,
    voiceLauncherContext?.patientId,
    voiceLauncherContext?.targetRole,
    voiceLauncherContext?.eventType,
    voiceLauncherContext?.eventTypesCsv,
    voiceLauncherContext?.allowedDestinationTypes,
  ])

  async function handleCheckForUpdates() {
    if (!reSendAppUpdateAction || isCheckingAppUpdate) {
      return
    }

    setIsCheckingAppUpdate(true)
    try {
      await reSendAppUpdateAction()
      setLastCheckedForUpdateAt(dayjs())
      toast.success('Checked for app updates.')
    } catch (error) {
      console.error('Failed to check for app updates', error)
      toast.error('Unable to check for updates right now.')
    } finally {
      setIsCheckingAppUpdate(false)
    }
  }

  function handleFloatingButtonClick() {
    if (hasActiveVoiceCall) {
      setSelectedTab('calls')
      setOpen(true)
      return
    }
    setOpen((prev) => {
      const nextOpen = !prev
      if (nextOpen && selectedTab === 'chat') {
        ensureChatBootstrapped()
      }
      return nextOpen
    })
  }

  function handleTabSelect(nextTab) {
    setSelectedTab(nextTab)
    if (nextTab === 'chat') {
      ensureChatBootstrapped()
    }
  }

  return (
    <>
      {floatingButtonVisible ? (
        <>
          <style>{portalVoiceMotionStyles}</style>
          <FIButtonContainer onClick={handleFloatingButtonClick} rotated={open}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {open ? (
                <KeyboardArrowDownIcon style={{ fontSize: '32px', rotate: '-22.5deg' }} />
              ) : showVoiceLauncherTab ? (
                <PhoneInTalkRoundedIcon style={{ fontSize: '26px' }} />
              ) : (
                <TelegramIcon style={{ fontSize: '28px' }} />
              )}
              {unreadCount > 0 && !open && (
                <div
                  style={{
                    background: 'red',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '16px',
                    height: '16px',
                    fontSize: '12px',
                    borderRadius: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {unreadCount}
                </div>
              )}
            </div>
          </FIButtonContainer>
          <FIContentContainer opened={open}>
            <div
              style={{
                height: intercomState === 'conversationList' ? 'calc(100% - 75px)' : '100%',
                width: '100%',
              }}
            >
              {selectedTab === 'calls' && (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                    padding: '20px',
                    background: '#f8fafc',
                    overflowY: 'auto',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}
                  >
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>
                      Calls
                    </div>
                    <div style={{ color: '#475569', lineHeight: 1.5 }}>
                      {voicePanelDescription}
                    </div>
                  </div>

                  {hasActiveVoiceCall || hasSettledVoiceCall ? (
                    <div
                      style={{
                        background: '#ffffff',
                        border: '1px solid rgba(226, 232, 240, 1)',
                        borderRadius: '22px',
                        padding: '18px',
                        boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '16px',
                          alignItems: 'stretch',
                        }}
                      >
                        <VoiceWaveform
                          status={voiceCallState?.status}
                          accentColor={voiceStatusPresentation.color}
                        />
                        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span
                              style={floatingChipStyles(
                                voiceStatusPresentation.border,
                                voiceStatusPresentation.background,
                                voiceStatusPresentation.color,
                              )}
                            >
                              {voiceStatusPresentation.label}
                            </span>
                            {voiceCallState?.providerType ? (
                              <span style={floatingChipStyles('#2563eb', '#eff6ff', '#1d4ed8')}>
                                {getProviderBadge(voiceCallState.providerType)}
                              </span>
                            ) : null}
                            {voiceCallState?.recordingEnabled ? (
                              <span style={floatingChipStyles('#0891b2', '#ecfeff', '#0e7490')}>
                                Recording enabled
                              </span>
                            ) : null}
                          </div>

                          <div
                            style={{
                              marginTop: '12px',
                              fontSize: '20px',
                              fontWeight: 800,
                              color: '#0f172a',
                              lineHeight: 1.25,
                            }}
                          >
                            {[voiceCallState?.destinationLabel, voiceCallState?.maskedDestination]
                              .filter(Boolean)
                              .join(' • ') || 'Connected participant'}
                          </div>

                          <div
                            style={{
                              marginTop: '8px',
                              color: '#475569',
                              lineHeight: 1.55,
                            }}
                          >
                            {hasActiveVoiceCall
                              ? 'The call drawer stays open so you can track progress and control the call without reopening it.'
                              : 'You can dismiss this summary or place another call from here.'}
                          </div>

                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(126px, 1fr))',
                              gap: '10px',
                              marginTop: '16px',
                            }}
                          >
                            <div
                              style={{
                                borderRadius: '16px',
                                background: '#f8fafc',
                                border: '1px solid rgba(226, 232, 240, 1)',
                                padding: '12px',
                              }}
                            >
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>
                                {voiceCallState?.status === 'connected' ? 'Live time' : 'Status'}
                              </div>
                              <div
                                style={{
                                  marginTop: '6px',
                                  fontSize: '18px',
                                  fontWeight: 800,
                                  color: '#0f172a',
                                }}
                              >
                                {voiceCallState?.status === 'connected' &&
                                voiceCallState?.elapsedSeconds >= 0
                                  ? formatElapsedTime(voiceCallState.elapsedSeconds)
                                  : voiceStatusPresentation.label}
                              </div>
                            </div>
                            <div
                              style={{
                                borderRadius: '16px',
                                background: '#f8fafc',
                                border: '1px solid rgba(226, 232, 240, 1)',
                                padding: '12px',
                              }}
                            >
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>
                                Time remaining
                              </div>
                              <div
                                style={{
                                  marginTop: '6px',
                                  fontSize: '18px',
                                  fontWeight: 800,
                                  color: voiceCallState?.remainingSeconds > 0 ? '#0f172a' : '#94a3b8',
                                }}
                              >
                                {voiceCallState?.remainingSeconds > 0
                                  ? formatRemainingTime(voiceCallState.remainingSeconds)
                                  : 'Ending soon'}
                              </div>
                            </div>
                            {voiceCallState?.renewalEnabled ? (
                              <div
                                style={{
                                  borderRadius: '16px',
                                  background: '#f8fafc',
                                  border: '1px solid rgba(226, 232, 240, 1)',
                                  padding: '12px',
                                }}
                              >
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>
                                  Extension
                                </div>
                                <div
                                  style={{
                                    marginTop: '6px',
                                    fontSize: '16px',
                                    fontWeight: 800,
                                    color: voiceCallState?.renewalAllowed ? '#15803d' : '#475569',
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {voiceCallState?.renewalAllowed
                                    ? `+${formatMinutesLabel(voiceCallState?.renewalIncrementSeconds)}`
                                    : 'Unavailable'}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {voiceCallState?.error ? (
                        <div style={{ marginTop: '12px', color: '#dc2626', lineHeight: 1.5 }}>
                          {voiceCallState.error}
                        </div>
                      ) : null}

                      {hasActiveVoiceCall ? (
                        <div
                          style={{
                            display: 'flex',
                            gap: '10px',
                            marginTop: '16px',
                          }}
                        >
                          {voiceCallState?.renewalEnabled ? (
                            <button
                              type="button"
                              onClick={handleVoiceCallExtend}
                              disabled={!voiceCallState?.renewalAllowed || voiceCallState?.renewalInFlight}
                              style={floatingActionButtonStyles({
                                primary: false,
                                disabled:
                                  !voiceCallState?.renewalAllowed || voiceCallState?.renewalInFlight,
                              })}
                            >
                              {voiceCallState?.renewalInFlight
                                ? 'Extending...'
                                : `Extend by ${formatMinutesLabel(
                                    voiceCallState?.renewalIncrementSeconds,
                                  )}`}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={handleVoiceCallEnd}
                            style={floatingActionButtonStyles({ primary: true, tone: 'danger' })}
                          >
                            End call
                          </button>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            gap: '10px',
                            marginTop: '16px',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => dismissPortalVoiceCall()}
                            style={floatingActionButtonStyles({ primary: false })}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  ) : voiceRtcConflict ? (
                    <div
                      style={{
                        background: '#fff7ed',
                        border: '1px solid rgba(251, 191, 36, 0.35)',
                        borderRadius: '18px',
                        padding: '16px',
                        color: '#9a3412',
                        boxShadow: '0 14px 30px rgba(249, 115, 22, 0.08)',
                      }}
                    >
                      <div style={{ fontSize: '16px', fontWeight: 700 }}>
                        {voiceRtcConflict?.title || 'Finish the video call first'}
                      </div>
                      <div style={{ marginTop: '8px', lineHeight: 1.5 }}>
                        {voiceRtcConflict?.description ||
                          'The current video call is already using the microphone. End it first, then place a phone call from here.'}
                      </div>
                      {voiceRtcConflict?.helperText ? (
                        <div style={{ marginTop: '10px', color: '#7c2d12', lineHeight: 1.5 }}>
                          {voiceRtcConflict.helperText}
                        </div>
                      ) : null}
                      {typeof voiceRtcConflict?.onResolve === 'function' ? (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await voiceRtcConflict.onResolve()
                              toast.info(
                                voiceRtcConflict?.successMessage ||
                                  'Video call ended. You can place the phone call now.',
                              )
                            } catch (error) {
                              toast.error(
                                error?.message ||
                                  voiceRtcConflict?.errorMessage ||
                                  'Unable to end the current video call right now.',
                              )
                            }
                          }}
                          style={{
                            ...floatingActionButtonStyles({ primary: true }),
                            marginTop: '14px',
                          }}
                        >
                          {voiceRtcConflict?.actionLabel || 'End video call'}
                        </button>
                      ) : null}
                    </div>
                  ) : voiceLoadingOptions ? (
                    <div style={floatingEmptyStateStyles}>
                      Loading calling options...
                    </div>
                  ) : voiceOptionsState?.error ? (
                    <div style={{ ...floatingEmptyStateStyles, color: '#b91c1c' }}>
                      {voiceOptionsState.error}
                    </div>
                  ) : filteredVoiceRoutes.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {filteredVoiceRoutes.map((route) => {
                        const destinationType = route?.destinationType || route?.id
                        const secondaryText = route?.enabled
                          ? [
                              getProviderBadge(route?.providerType),
                              route?.destinationLabel,
                              route?.maskedDestination,
                            ]
                              .filter(Boolean)
                              .join(' • ')
                          : route?.disabledReason || 'This route is currently unavailable.'

                        return (
                          <button
                            key={destinationType}
                            type="button"
                            disabled={!route?.enabled || Boolean(voiceLoadingRoute)}
                            onClick={() => route?.enabled && handleVoiceRouteSelect(route)}
                            style={{
                              ...floatingRouteCardStyles,
                              opacity:
                                !route?.enabled || Boolean(voiceLoadingRoute) ? 0.6 : 1,
                              cursor:
                                !route?.enabled || Boolean(voiceLoadingRoute)
                                  ? 'not-allowed'
                                  : 'pointer',
                            }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  gap: '12px',
                                  alignItems: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '42px',
                                      height: '42px',
                                      borderRadius: '14px',
                                      background: route?.enabled ? '#eff6ff' : '#f8fafc',
                                      color: route?.enabled ? '#1d4ed8' : '#94a3b8',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flex: '0 0 auto',
                                    }}
                                  >
                                    <LocalPhoneOutlinedIcon style={{ fontSize: '20px' }} />
                                  </div>
                                  <div
                                    style={{
                                      minWidth: 0,
                                      flex: '1 1 auto',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '15px',
                                        fontWeight: 700,
                                        color: '#0f172a',
                                        textAlign: 'left',
                                      }}
                                    >
                                      {route?.title || route?.destinationLabel || destinationType}
                                    </div>
                                    <div
                                      style={{
                                        marginTop: '6px',
                                        color: route?.enabled ? '#64748b' : '#b45309',
                                        lineHeight: 1.5,
                                        textAlign: 'left',
                                      }}
                                    >
                                      {secondaryText}
                                    </div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    flex: '0 0 auto',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '92px',
                                    minHeight: '38px',
                                    padding: '0 12px',
                                    borderRadius: '12px',
                                    background:
                                      voiceLoadingRoute === destinationType
                                        ? '#dbeafe'
                                        : route?.enabled
                                          ? '#1d4ed8'
                                          : '#e2e8f0',
                                    color:
                                      voiceLoadingRoute === destinationType || route?.enabled
                                        ? '#ffffff'
                                        : '#64748b',
                                    fontSize: '13px',
                                    fontWeight: 800,
                                  }}
                                >
                                  {voiceLoadingRoute === destinationType
                                    ? 'Calling...'
                                    : route?.enabled
                                      ? 'Call now'
                                      : 'Unavailable'}
                                </div>
                              </div>
                            </button>
                          )
                      })}
                    </div>
                  ) : (
                    <div style={floatingEmptyStateStyles}>
                      {voiceLauncherContext?.clinicBrandId && voiceLauncherContext?.clinicId
                        ? 'No calling options are currently enabled for this clinic.'
                        : voiceLauncherContext?.bookedEventId
                          ? 'No calling options are currently enabled for this session.'
                          : showVoiceLauncherTab
                            ? 'Calls appear here when this screen provides a supported clinic or session context.'
                            : 'No calling options are available here right now.'}
                    </div>
                  )}
                </div>
              )}
              {selectedTab === 'threads' && isSupportPortalPath && (
                <SupportWhatsappThreadsPanel />
              )}
              {selectedTab === 'chat' && (
                <>
                  {showChatLoadingState && (
                    <div
                      style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '24px',
                        textAlign: 'center',
                        color: '#64748b',
                        fontWeight: 600,
                      }}
                    >
                      Loading chat...
                    </div>
                  )}
                  {showNoConversationsState && !showChatLoadingState && (
                    <div
                      style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '24px',
                        textAlign: 'center',
                        color: '#64748b',
                        fontWeight: 600,
                      }}
                    >
                      No conversations available right now.
                    </div>
                  )}
                  {!showChatLoadingState &&
                    !showNoConversationsState &&
                    intercomState === 'conversationList' && (
                    <ConversationListChatUI
                      closeFunction={width < 768 ? () => setOpen(false) : undefined}
                      choseClickedPersonFunc={(data) => {
                        setIntercomState('chosenConversation')
                        setChosenPersonChatId(data)
                      }}
                      listItems={contacts}
                      userPresenceList={userOnlineList}
                      arraysOfMessages={conversationsWithContacts || []}
                      updateUnreadGlobalCountFunc={(data) => setUnreadCount(data)}
                    />
                  )}
                  {!showChatLoadingState &&
                    !showNoConversationsState &&
                    intercomState === 'chosenConversation' && (
                    <TwoPeopleChatUI
                      agoraChatServerConnection={agoraChatServerConnection}
                      thisUserChatId={agoraChatId}
                      thisUserChatToken={agoraChatToken}
                      thisUserNickname={profileData?.name}
                      thisUserAvatarUrl={
                        profileData?.profilePicId
                          ? getImageLink(profileData?.profilePicId)
                          : undefined
                      }
                      otherUserChatId={chosenPersonChatId}
                      otherUserName={otherChosenUser?.name || otherChosenUser?.firstName}
                      otherUserPresence={
                        userOnlineList?.find((item) =>
                          isSameUserId(item?.userId, chosenPersonChatId),
                        )?.online || false
                      }
                      otherUserRelativeRole={otherChosenUser?.relativeRole}
                      otherUserAvatarUrl={
                        otherChosenUser?.profilePicId
                          ? getImageLink(otherChosenUser?.profilePicId)
                          : undefined
                      }
                      arrayOfAllMessages={conversationsWithContacts?.[chosenPersonChatId] || []}
                      addLocalMessageToArray={(data) => {
                        setConversationsWithContacts((prev) => ({
                          ...prev,
                          [chosenPersonChatId]: [...(prev[chosenPersonChatId] || []), data],
                        }))
                        setScrollTriggerBoolean((prev) => !prev)
                      }}
                      showNotificationsOnIncomingMessage={false}
                      backFunction={() => {
                        setIntercomState('conversationList')
                        setChosenPersonChatId()
                      }}
                      scrollTriggerBoolean={scrollTriggerBoolean}
                      chatsLoading={chatsLoading}
                      loadOlderChatsWithUserFunc={(otherUserId) => loadMoreChats(otherUserId)}
                      replaceSpecificTempMessageWithResultMessage={(
                        userId,
                        tempMsgIdToReplace,
                        resultMessage,
                      ) =>
                        replaceSpecificTempMessageWithResultMessage(
                          userId,
                          tempMsgIdToReplace,
                          resultMessage,
                        )
                      }
                      canAttachFeatures={
                        localStorage.getItem('authorities') &&
                        (getAllRolesInBoolean().isAdmin || getAllRolesInBoolean().isTeacher)
                      }
                    />
                  )}
                </>
              )}
              {selectedTab === 'info' && (
                <div style={{ display: 'unset' }}>
                  <iframe
                    id="helpcenter"
                    title="Help Center"
                    src={
                      window.location.protocol + '//' + window.location.host + '/help-center-embedded'
                    }
                    height="100%"
                    width="100%"
                  ></iframe>
                </div>
              )}
              {selectedTab === 'app' && (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '20px',
                    background: '#f8fafc',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                      Installed App Refresh
                    </div>
                    <div style={{ marginTop: '8px', color: '#475569', lineHeight: 1.5 }}>
                      Use this when the installed app looks stale or you need a stronger reload than
                      a normal page refresh.
                    </div>
                    <div
                      style={{
                        marginTop: '20px',
                        padding: '14px 16px',
                        borderRadius: '14px',
                        background: '#e2e8f0',
                        color: '#1e293b',
                        lineHeight: 1.55,
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>Version {appVersion}</div>
                      <div style={{ marginTop: '4px' }}>Build {lastBuildDate}</div>
                      {lastCheckedForUpdateAt ? (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#475569' }}>
                          Last checked at {lastCheckedForUpdateAt.format('hh:mm A')}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={handleCheckForUpdates}
                      disabled={isCheckingAppUpdate || isAppRefreshInProgress}
                      style={{
                        width: '100%',
                        minHeight: '44px',
                        border: 'none',
                        borderRadius: '12px',
                        background: '#0f766e',
                        color: '#fff',
                        fontWeight: 700,
                        cursor:
                          isCheckingAppUpdate || isAppRefreshInProgress ? 'not-allowed' : 'pointer',
                        opacity: isCheckingAppUpdate || isAppRefreshInProgress ? 0.7 : 1,
                      }}
                    >
                      {isCheckingAppUpdate ? 'Checking for updates...' : 'Check for update'}
                    </button>

                    <button
                      type="button"
                      onClick={() => requestAppRefresh?.()}
                      disabled={!requestAppRefresh || isAppRefreshInProgress || isCheckingAppUpdate}
                      style={{
                        width: '100%',
                        minHeight: '44px',
                        border: '1px solid #94a3b8',
                        borderRadius: '12px',
                        background: '#fff',
                        color: '#0f172a',
                        fontWeight: 700,
                        cursor:
                          !requestAppRefresh || isAppRefreshInProgress || isCheckingAppUpdate
                            ? 'not-allowed'
                            : 'pointer',
                        opacity:
                          !requestAppRefresh || isAppRefreshInProgress || isCheckingAppUpdate
                            ? 0.7
                            : 1,
                        marginTop: '12px',
                      }}
                    >
                      {isAppRefreshInProgress ? 'Refreshing app...' : 'Refresh installed app'}
                    </button>

                    <div
                      style={{
                        marginTop: '12px',
                        color: '#64748b',
                        lineHeight: 1.45,
                        fontSize: '13px',
                      }}
                    >
                      This refresh clears browser caches, unregisters service workers, and reloads
                      using a cache-busting URL.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {intercomState === 'conversationList' && (
              <div
                style={{
                  background: '#fff',
                  height: '75px',
                  display: 'flex',
                  justifyContent: 'space-around',
                  alignItems: 'center',
                  borderTop: '1px solid rgb(182, 194, 200)',
                }}
              >
                {showVoiceLauncherTab && (
                  <IntercomButton
                    SelectedIcon={PhoneInTalkRoundedIcon}
                    NotSelectedIcon={LocalPhoneOutlinedIcon}
                    text={'Calls'}
                    selected={selectedTab === 'calls'}
                    clickFunc={() => handleTabSelect('calls')}
                  />
                )}
                {isSupportPortalPath && (
                  <IntercomButton
                    SelectedIcon={HistoryEduRoundedIcon}
                    NotSelectedIcon={HistoryEduRoundedIcon}
                    text={'Threads'}
                    selected={selectedTab === 'threads'}
                    clickFunc={() => handleTabSelect('threads')}
                  />
                )}
                <IntercomButton
                  SelectedIcon={ChatIcon}
                  NotSelectedIcon={ChatOutlinedIcon}
                  text={'Chat'}
                  selected={selectedTab === 'chat'}
                  clickFunc={() => handleTabSelect('chat')}
                />
                <IntercomButton
                  SelectedIcon={InfoIcon}
                  NotSelectedIcon={InfoOutlinedIcon}
                  text={'Help'}
                  selected={selectedTab === 'info'}
                  clickFunc={() => handleTabSelect('info')}
                />
                {isStandaloneInstalledApp && (
                  <IntercomButton
                    SelectedIcon={SystemUpdateAltRoundedIcon}
                    NotSelectedIcon={RefreshRoundedIcon}
                    text={'App'}
                    selected={selectedTab === 'app'}
                    clickFunc={() => handleTabSelect('app')}
                  />
                )}
              </div>
            )}
          </FIContentContainer>
        </>
      ) : (
        <></>
      )}
    </>
  )
}

// showing floating button on limited paths --- done
// connection data pass from outside --- done
// ensuring compatibility with video call chat - (that part also makes connection with agora itself) --- done
// showing online even in the list : subscribe to presence and even receive presence events from all people outside twopeoplelist. also publish online status outside twopeoplelist
// new message number on the floating button
// last message - not always - but at least when the message just came
// context base chat - api changes

export async function makeAndStoreAgoraConnectionInRedux(dispatch, appKey, userChatId) {
  console.log('setting redux data of connection, userchatid, userchattoken')

  // we should already have user id, we get and set the token, we make connection
  let chatToken
  const chatTokenResponse = await getAgoraChatToken()
  if (
    chatTokenResponse?.status === 200 &&
    isCommonResponseSuccessful(chatTokenResponse?.data?.code) &&
    chatTokenResponse?.data?.data?.chatToken
  ) {
    chatToken = chatTokenResponse?.data?.data?.chatToken
  }
  if (dispatch && appKey && userChatId && chatToken) {
    const client = new AC.connection({ appKey })
    await client.open({
      user: userChatId,
      agoraToken: chatToken,
    })
    // set connection:
    dispatch(setAgoraChatServerConnection(client))
    // setUserId
    dispatch(setAgoraChatId(userChatId))
    // setChatToken
    dispatch(setAgoraChatToken(chatToken))
  } else {
    console.log('can not make agora connection without all 4 parameters')
  }
}

export const IntercomButton = ({ SelectedIcon, NotSelectedIcon, text, selected, clickFunc }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        transition: 'all 0.2s ease',
      }}
      onClick={clickFunc}
    >
      {selected ? (
        <SelectedIcon style={{ fontSize: '24px', cursor: 'pointer', color: '#1E323B' }} />
      ) : (
        <NotSelectedIcon style={{ fontSize: '24px', cursor: 'pointer', color: '#1E323B' }} />
      )}
      <div style={{ fontSize: '16px', fontWeight: selected ? '600' : '300' }}>{text}</div>
    </div>
  )
}
